import { criterion, fromCriteria, unsupported } from "@/core/check";
import type { Lesson } from "@/core/types";
import { countTrackedChanges, isTrackingOn, readChangeTrackingMode, readComments } from "@/office/word/review";
import { fail } from "@/core/check";
import { pruefeMitMakro } from "@/office/makro/pruefung";
import { MAKRO_PDF } from "../makros";

export const lektionVorDemVersand: Lesson = {
  id: "vor-dem-versand",
  title: "Vor dem Versand aufräumen",
  summary: "Der Kontrollgang, der peinliche Überraschungen verhindert.",
  durationMinutes: 9,
  learningGoals: [
    "Ein Dokument vor der Weitergabe vollständig bereinigen",
    "Die Dokumentprüfung einsetzen",
    "Verborgene Informationen erkennen",
  ],
  steps: [
    {
      id: "dokumentpruefung",
      title: "Die Dokumentprüfung",
      explanation: [
        "Ein Word-Dokument enthält mehr, als auf dem Bildschirm zu sehen ist: " +
          "aufgezeichnete Änderungen, aufgelöste Kommentare, der Name der verfassenden " +
          "Person, frühere Bearbeitende, manchmal ausgeblendeter Text.",
        "Wird ein Angebot an einen Kunden weitergegeben, das noch die interne Diskussion " +
          "über den Preisnachlass enthält, ist das ein echtes Problem. Genau dafür gibt es " +
          "die Dokumentprüfung: „Datei“ → „Informationen“ → „Auf Probleme überprüfen“ → " +
          "„Dokument prüfen“.",
        "Word listet auf, was gefunden wurde, und bietet zu jedem Punkt „Alle entfernen“ " +
          "an. Das lässt sich nicht rückgängig machen – deshalb vorher speichern oder mit " +
          "einer Kopie arbeiten.",
      ],
      keyPoints: [
        "Datei → Informationen → Auf Probleme überprüfen → Dokument prüfen.",
        "Das Entfernen ist endgültig – vorher eine Kopie anlegen.",
        "Für den endgültigen Versand ist ein PDF meist die bessere Wahl.",
      ],
      task: {
        instruction:
          "Mache dein Dokument versandfertig: keine offenen nachverfolgten Änderungen, " +
          "keine unaufgelösten Kommentare, Nachverfolgung ausgeschaltet.",
        hint: "Kommentare löschst du über Überprüfen → Löschen → „Alle Kommentare im Dokument löschen“.",
        check: async () => {
          const modus = await readChangeTrackingMode();
          const kommentare = await readComments();
          const offen = await countTrackedChanges();

          if (modus === null && kommentare === null) {
            return unsupported(
              "Diese Word-Version stellt dem Add-in weder Kommentare noch den Zustand der " +
                "Änderungsnachverfolgung zur Verfügung.",
              "Gehe die Punkte selbst durch und markiere den Schritt danach als erledigt.",
            );
          }

          const kriterien = [];
          if (modus !== null) {
            kriterien.push(
              criterion(
                `Die Änderungsnachverfolgung ist ausgeschaltet (Zustand: ${modus})`,
                !isTrackingOn(modus),
              ),
            );
          }
          if (offen !== null) {
            kriterien.push(
              criterion(`Keine offenen nachverfolgten Änderungen (gefunden: ${offen})`, offen === 0),
            );
          }
          if (kommentare !== null) {
            const unaufgeloest = kommentare.filter((kommentar) => !kommentar.resolved);
            kriterien.push(
              criterion(
                `Keine unaufgelösten Kommentare (gefunden: ${unaufgeloest.length})`,
                unaufgeloest.length === 0,
              ),
            );
          }

          return fromCriteria(kriterien, {
            success: "Das Dokument kann raus – es enthält nichts Internes mehr.",
            failure: "Vor dem Versand ist noch etwas zu tun:",
            hint: "Denk auch an Kopf- und Fußzeilen: Auch dort können Kommentare und Änderungen liegen.",
          });
        },
      },
    },
    {
      id: "als-pdf",
      title: "Der letzte Schritt: PDF",
      explanation: [
        "Für die Weitergabe nach außen ist ein PDF fast immer die bessere Wahl. Es " +
          "enthält keine nachverfolgten Änderungen, keine Kommentare und keine " +
          "Bearbeitungshistorie – und es sieht bei allen Empfängern gleich aus, " +
          "unabhängig von installierten Schriftarten und Word-Version.",
        "Word erzeugt es direkt: „Datei“ → „Exportieren“ → „PDF/XPS-Dokument erstellen“, " +
          "oder beim Speichern unter das Dateiformat PDF wählen.",
        "Ein Hinweis für später: Wenn dein Dokument ein Inhaltsverzeichnis aus dem " +
          "Formatvorlagen-Modul enthält, werden daraus im PDF anklickbare Sprungmarken – " +
          "vorausgesetzt, du hast echte Überschriftenvorlagen verwendet. Auch hier zahlt " +
          "sich die saubere Struktur noch einmal aus.",
      ],
      keyPoints: [
        "Datei → Exportieren → PDF/XPS-Dokument erstellen.",
        "Das PDF enthält keine Kommentare und keine Änderungshistorie.",
        "Überschriftenvorlagen werden im PDF zu Lesezeichen und Sprungmarken.",
      ],
      task: {
        instruction:
          "Speichere dein Dokument und exportiere es danach als PDF in denselben Ordner, mit " +
          "demselben Dateinamen.",
        hint: "Datei → Exportieren → PDF/XPS-Dokument erstellen → Ordner und Namen unverändert lassen → Veröffentlichen.",
        macro: MAKRO_PDF,
        check: () =>
          pruefeMitMakro(MAKRO_PDF, (bericht) => {
            if (bericht.janein("gespeichert") !== true) {
              return fail("Das Dokument ist noch nicht gespeichert.", "Erst speichern, dann als PDF exportieren.");
            }
            if (bericht.janein("pfadLesbar") !== true) {
              return unsupported(
                "Das Dokument liegt in OneDrive oder SharePoint. Dort kann das Prüfmakro den Ordner nicht durchsuchen.",
                "Prüfe selbst, ob die PDF-Datei neben dem Dokument liegt. Danach kannst du den Schritt selbst als erledigt markieren.",
              );
            }
            return fromCriteria(
              [criterion("Neben dem Dokument liegt eine gleichnamige PDF-Datei", bericht.janein("pdf") === true)],
              {
                success: `PDF gefunden${bericht.text("pdfZeit") ? ` (erstellt ${new Date(bericht.text("pdfZeit") ?? "").toLocaleString("de-DE")})` : ""}.`,
                failure: "Neben dem Dokument liegt noch keine PDF-Datei mit demselben Namen.",
                hint: `Gesucht wird: ${bericht.text("datei") ?? "Dokumentname.pdf"}`,
              },
            );
          }),
      },
    },
  ],
};
